import React, { useEffect, useState } from 'react';
import classNames from 'classnames/bind';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import _ from 'lodash';
import { Button } from 'antd';

import BasicTable from 'components/_shared/Table';
import { Redux } from 'helpers/types/_common';
import { TABLE_NAMES } from 'helpers/constants/Tables/tableList';
import { SpecialistsRow } from 'api/Specialists/types';
import EmployeeDrawer from 'components/Drawers/EmployeeDrawer';
import EngKnowledgeLevel from 'components/_shared/EngKnowledgeLevel';
import OCCUPATIONS from 'helpers/constants/Occupations/Occupations';
import { UserDetails } from 'api/User/types';
import buttons from 'helpers/styles/components/buttons.module.sass';
import { Meta, Sorter } from 'api/_request/types';

import { RequestSettingsState } from '../../helpers/types';
import { MAX_ELEMENTS_FOR_SCROLL } from '../../../../../../helpers/constants';
import { attitudesColumns, updateColumns } from '../../../../../../helpers/helpers';
import S from './helpers/styles.module.sass';
import { DEFAULT_PAGINATION_RAW } from '../../../../../../../../helpers/constants/_common/Pagination';
import TrackerLogo from '../../../../../../../_shared/TrackerLogo/TrackerLogo';
import { EmployeeDrawerData } from '../../../../../../../Drawers/EmployeeDrawer/helpers/types';

const cx = classNames.bind(S);

export interface FindSpecialistsTableProps {
  settings: RequestSettingsState;
  fetchRequestSpecialists: (pagination: Meta, sorter?: Sorter) => void;
  handleAddToCandidate: (userId: number) => Promise<any>;
  isViewMode: boolean;
}

const RequestTable: React.FC<FindSpecialistsTableProps> = ({ settings, fetchRequestSpecialists, handleAddToCandidate, isViewMode }) => {
  const { meta, objects, loading, sorter } = useSelector((state: Redux) => state.tables[TABLE_NAMES.REQUEST_SPECIALISTS]);

  const [ candidateId, setCandidateId ] = useState<number | null>(null);
  const [ drawerCandidateLoading, setDrawerCandidateLoading ] = useState(false);
  const [ employeeDrawerData, setEmployeeDrawerData ] = useState<EmployeeDrawerData>({ userId: undefined, visible: false });

  useEffect(() => {
    if (settings?.requestData) {
      fetchRequestSpecialists(DEFAULT_PAGINATION_RAW, sorter);
    }
  }, [
    settings.requestData, settings.searchValue, settings.skillsColumns,
    settings.entityId, settings.userId, settings.partnerId,
  ]);

  const onCell = (record: SpecialistsRow) => ({
    onClick: () => {
      setEmployeeDrawerData({ userId: record.userId, visible: true });
    },
  });

  const renderColumns = {
    fullName: {
      onCell,
      render: (text: string, record: SpecialistsRow) => {
        const logo = record.avatarUrl
          ? <img src={record.avatarUrl} alt="avatar" className={cx('author-avatar')} />
          : <TrackerLogo className={cx('author-avatar', 'author-avatar--qs')} />;

        return (
          <div className={cx('name-cell')}>
            {logo}
            {`${record.forename} ${record.surname}`}
          </div>
        );
      },
    },
    occupation: {
      render: (text: string) => OCCUPATIONS[text].name,
      onCell,
    },

    englishKnowledge: {
      render: (engLevel: string) => <EngKnowledgeLevel englishKnowledgeLevel={engLevel} />,
      onCell,
    },

    ...attitudesColumns(settings.skills, onCell),
  };

  const onCloseDrawer = () => {
    setEmployeeDrawerData({ userId: undefined, visible: false });
    setDrawerCandidateLoading(false);
  };

  const handleAddCandidate = (record: SpecialistsRow) => {
    if (candidateId) {
      return;
    }

    setCandidateId(record.userId);

    return handleAddToCandidate(record.userId)
      .finally(() => {
        setCandidateId(null);
      });
  };

  const rowSelection = {
    renderCell: (checked: boolean, record: SpecialistsRow) => (
      <FontAwesomeIcon
        icon={record.userId === candidateId ? 'spinner' : [ 'fas', 'star' ]}
        className={cx('star-select', { 'star-select--loading': record.userId === candidateId })}
        onClick={() => handleAddCandidate(record)}
        spin={record.userId === candidateId}
      />
    ),
    columnTitle: <FontAwesomeIcon icon={[ 'fas', 'star' ]} style={{ color: '#727981' }} />,
    preserveSelectedRowKeys: true,
    selectedRowKeys: [],
  };

  const drawerCandidateButton = (user?: UserDetails) => {
    if (isViewMode) {
      return null;
    }

    const addToCandidate = () => {
      if (!user?.userId) {
        return;
      }
      setDrawerCandidateLoading(true);

      return handleAddToCandidate(user.userId)
        .finally(() => onCloseDrawer());
    };

    return (
      <Button
        className={buttons.qsButtonPrimary}
        type="primary"
        key={user?.userId}
        loading={drawerCandidateLoading}
        onClick={addToCandidate}
      >
        <div className={buttons.iconTextWrapper}>
          <FontAwesomeIcon icon="star" />
          Add to Candidates
        </div>
      </Button>
    );
  };

  return (
    <div className={cx('request-detail-table')}>
      <div className={cx('request-detail-table_total')}>
        { meta?.totalCount ? `${meta.totalCount} Engineers Found` : ''}
      </div>
      <div>
        <BasicTable
          tableName={TABLE_NAMES.REQUEST_SPECIALISTS}
          initialRequest={false}
          getData={fetchRequestSpecialists}
          rowKey="userId"
          columns={updateColumns(false, settings.skillsColumns)}
          className={cx('request-detail-table_table', { 'no-data': _.isEmpty(objects) })}
          pagination={meta}
          loading={loading}
          dataSource={objects}
          sortDirections={[ 'descend', 'ascend' ]}
          scroll={{ x: 1000, y: objects.length > MAX_ELEMENTS_FOR_SCROLL ? '45vh' : undefined }}
          rowClassName={cx('bordered-row')}
          rowSelection={isViewMode ? undefined : rowSelection}
          renderColumns={renderColumns}
        />
      </div>

      <EmployeeDrawer
        drawerData={employeeDrawerData}
        onClose={onCloseDrawer}
        externalButtons={[ drawerCandidateButton ]}
      />
    </div>
  );
};

export default RequestTable;
